import { useId, useMemo, useState } from 'react';
import type { NurseryCustomer } from '../../types/nursery';

/**
 * Choose a customer by typing: part of a name, a head office, or a store
 * number. Arrow keys move through the matches and Enter takes the highlighted
 * one (the first when none is), so an order can be started without the mouse.
 */

export interface CustomerPickerProps {
  id: string;
  customers: NurseryCustomer[];
  value: number | null;
  onChange: (customerId: number | null) => void;
  /** Shown read-only: the order exists and its prices belong to this customer. */
  locked?: boolean;
}

const MAX_SHOWN = 8;

/** A site reads "<head office> — <site> (#<store number>)"; an account reads by its own name. */
const customerLabel = (c: NurseryCustomer): string => {
  const code = c.customer_code ? ` (#${c.customer_code})` : '';
  if (c.parent_name) return `${c.parent_name} — ${c.name}${code}`;
  return `${c.name}${code}${c.tier_name ? ` — ${c.tier_name}` : ''}`;
};

export function CustomerPicker({ id, customers, value, onChange, locked = false }: CustomerPickerProps) {
  const listId = useId();
  const selected = useMemo(() => customers.find((c) => c.id === value) ?? null, [customers, value]);
  // null while the chosen customer's label is showing; a string once typing starts.
  const [typed, setTyped] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const text = typed ?? (selected ? customerLabel(selected) : '');

  const matches = useMemo(() => {
    const words = (typed ?? '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!words.length) return customers;
    return customers.filter((c) => {
      const hay = `${c.name} ${c.parent_name ?? ''} ${c.customer_code ?? ''}`.toLowerCase();
      return words.every((w) => hay.includes(w));
    });
  }, [customers, typed]);
  const shown = matches.slice(0, MAX_SHOWN);

  const pick = (c: NurseryCustomer) => {
    setTyped(null);
    setOpen(false);
    onChange(c.id);
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setOpen(true);
      if (!shown.length) return;
      setActive((i) => (e.key === 'ArrowDown' ? Math.min(i + 1, shown.length - 1) : Math.max(i - 1, 0)));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (open && shown.length) pick(shown[Math.min(active, shown.length - 1)]);
    } else if (e.key === 'Escape') {
      setTyped(null);
      setOpen(false);
    }
  };

  if (locked) {
    return (
      <input
        id={id}
        type="text"
        readOnly
        value={text}
        className="w-full border border-neutral-200 bg-neutral-50 text-neutral-700 rounded-lg px-3 py-2 text-sm truncate"
      />
    );
  }

  return (
    <div className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && shown.length ? `${listId}-${Math.min(active, shown.length - 1)}` : undefined}
        autoComplete="off"
        placeholder="Type a name or store number"
        value={text}
        onChange={(e) => {
          setTyped(e.target.value);
          setActive(0);
          setOpen(true);
          if (value != null) onChange(null);
        }}
        onFocus={(e) => { if (selected) e.target.select(); else setOpen(true); }}
        onBlur={() => { setOpen(false); if (selected) setTyped(null); }}
        onKeyDown={onKeyDown}
        className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
      />
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-neutral-200 rounded-lg shadow-lg py-1"
        >
          {shown.map((c, i) => (
            <li
              key={c.id}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === active}
              // mousedown, not click: the input's blur would close the list first.
              onMouseDown={(e) => { e.preventDefault(); pick(c); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer break-words ${i === active ? 'bg-primary-50 text-primary-900' : 'text-neutral-800'}`}
            >
              {customerLabel(c)}
            </li>
          ))}
          {shown.length === 0 && (
            <li className="px-3 py-2 text-sm text-neutral-500">No customer matches.</li>
          )}
          {matches.length > shown.length && (
            <li className="px-3 py-1.5 text-xs text-neutral-500">
              {matches.length - shown.length} more. Keep typing to narrow them.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
